<script setup lang="ts">
/**
 * About Zephyrwrite content (#350): name, version, release date, Copy version
 * and Check for updates (not on web). Shell for `AboutPanel.update.ts`.
 *
 * host "window": the native About window (`AboutPage.vue`); listens for the
 * menu's "Check for Updates…" while open.
 * host "dialog": the pop-up on web and mobile.
 */
import { computed, onBeforeUnmount, onMounted } from "vue";
import logoUrl from "../assets/logo.png";
import { assertNever } from "../lib/assertNever";
import {
  APP_NAME,
  appReleaseDate,
  appVersion,
  copyVersionText,
  isDevBuild,
  releaseDateText,
} from "../lib/appVersion";
import {
  checkManifest,
  fetchUpdateChannel,
  installAppUpdate,
  openUpdateDownload,
  restartAfterUpdate,
  type UpdateChannel,
} from "../lib/appUpdate";
import {
  checkBetaUpdateNow,
  dismissBetaVersion,
  ensureBetaSourceRoot,
  readBetaSourceRoot,
  startBetaUpdate,
} from "../lib/betaUpdate";
import {
  isJust,
  isNothing,
  just,
  mapMaybe,
  matchMaybe,
  nothing,
  type Maybe,
} from "../lib/maybe";
import { isTauri } from "../lib/runtime";
import { isMobileShell } from "../lib/tauri/core";
import {
  listenAboutCheckUpdates,
  takeAboutCheckRequest,
} from "../lib/tauri/about";
import { useTea } from "../lib/tea";
import ZwButton from "./ui/ZwButton.vue";
import {
  aboutCopyLabel,
  aboutErrorDetail,
  aboutPrimaryAction,
  aboutPrimaryBusy,
  aboutSecondaryAction,
  aboutShowsUpdates,
  aboutStatusText,
  aboutSupportFor,
  initialAboutPanelModel,
  manifestUrlFor,
  updateAboutPanelFn,
  type AboutHost,
  type AboutPanelCmd,
  type AboutPanelMsg,
} from "./AboutPanel.update";

const props = defineProps<{
  host: AboutHost;
  /** Start a check as soon as support is known ("Check for updates"). */
  checkOnOpen: boolean;
}>();

const version = appVersion();
const devBuild = isDevBuild();
const releaseDate = appReleaseDate();
const releaseLine = releaseDateText(releaseDate, devBuild);
const copyText = copyVersionText(version, releaseDate, devBuild);

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Flipped on close so an in-flight install does not start after the window goes away. */
const session: { alive: boolean } = { alive: true };

async function dispatchIfCurrent(msg: AboutPanelMsg): Promise<void> {
  if (session.alive === false) return;
  await dispatch(msg);
}

async function runCheck(manifestUrl: Maybe<string>): Promise<void> {
  if (isJust(manifestUrl)) {
    const result = await checkManifest(manifestUrl.value, version);
    switch (result.kind) {
      case "upToDate":
        await dispatchIfCurrent({ type: "CheckSucceeded", offer: nothing });
        return;
      case "available":
        await dispatchIfCurrent({
          type: "CheckSucceeded",
          offer: just(result.offer),
        });
        return;
      case "failed":
        await dispatchIfCurrent({
          type: "CheckFailed",
          reason: result.reason,
          message: result.message,
        });
        return;
      default:
        assertNever(result);
    }
  }
  const result = await checkBetaUpdateNow();
  if (result.ok) {
    await dispatchIfCurrent({
      type: "CheckSucceeded",
      offer: mapMaybe(result.value, (tag) => ({ version: tag, link: nothing })),
    });
  } else {
    await dispatchIfCurrent({
      type: "CheckFailed",
      reason: "failed",
      message: result.error,
    });
  }
}

async function runSourceUpdate(): Promise<void> {
  try {
    const root = await ensureBetaSourceRoot();
    if (session.alive === false) return;
    if (isNothing(root)) {
      await dispatchIfCurrent({
        type: "InstallFailed",
        message: "Choose the Zephyrwrite source folder to update.",
      });
      return;
    }
    await startBetaUpdate();
    await dispatchIfCurrent({ type: "InstallStarted" });
  } catch (error) {
    await dispatchIfCurrent({
      type: "InstallFailed",
      message: error instanceof Error ? error.message : String(error),
    });
  }
}

async function runAboutCmd(cmd: AboutPanelCmd): Promise<void> {
  switch (cmd.kind) {
    case "checkForUpdate":
      await runCheck(cmd.manifestUrl);
      return;
    case "startSourceUpdate":
      await runSourceUpdate();
      return;
    case "installInPlace": {
      const result = await installAppUpdate();
      if (result.ok) {
        await dispatchIfCurrent({
          type: "InstallFinished",
          installed: result.value,
        });
      } else {
        await dispatchIfCurrent({
          type: "InstallFailed",
          message: result.error,
        });
      }
      return;
    }
    case "openDownload": {
      const result = await openUpdateDownload(cmd.url);
      if (result.ok) {
        await dispatchIfCurrent({ type: "DownloadOpened" });
      } else {
        await dispatchIfCurrent({
          type: "InstallFailed",
          message: result.error,
        });
      }
      return;
    }
    case "restartApp":
      await restartAfterUpdate();
      return;
    case "dismissUpdate":
      dismissBetaVersion(cmd.version);
      return;
    case "copyText":
      await dispatchIfCurrent({
        type: "CopyFinished",
        copied: await copyToClipboard(cmd.text),
      });
      return;
    default:
      return assertNever(cmd);
  }
}

const { model, dispatch } = useTea(initialAboutPanelModel, updateAboutPanelFn, {
  runCmd: just(runAboutCmd),
});

const statusText = computed((): string => aboutStatusText(model.value));
const primary = computed(() => aboutPrimaryAction(model.value));
const primaryBusy = computed((): boolean => aboutPrimaryBusy(model.value));
const secondary = computed(() => aboutSecondaryAction(model.value));
const showLater = computed((): boolean => isJust(secondary.value));
const errorDetail = computed(() => aboutErrorDetail(model.value));
const showErrorDetail = computed((): boolean => isJust(errorDetail.value));
const errorDetailText = computed((): string =>
  isJust(errorDetail.value) ? errorDetail.value.value : "",
);
const showUpdates = computed((): boolean =>
  aboutShowsUpdates(model.value.support),
);
const copyLabel = computed((): string => aboutCopyLabel(model.value.copy));

function onPrimary(): void {
  const msg = primary.value.msg;
  if (isJust(msg)) void dispatch(msg.value);
}

function onLater(): void {
  const action = secondary.value;
  if (isNothing(action)) return;
  void dispatch(action.value.msg);
}

function onCopy(): void {
  void dispatch({ type: "CopyRequested", text: copyText });
}

const unlistenCheck: { current: Maybe<() => void> } = { current: nothing };

onMounted(async () => {
  const tauri = isTauri();
  const mobile = await isMobileShell();
  const askedByMenu =
    props.host === "window" && tauri
      ? await takeAboutCheckRequest().catch(() => false)
      : false;
  if (props.checkOnOpen || askedByMenu) {
    await dispatch({ type: "CheckRequested" });
  }
  const channel: UpdateChannel = tauri
    ? await fetchUpdateChannel()
    : { kind: "none" };
  const support = aboutSupportFor(tauri, mobile, channel);
  const sourceRoot = support === "mac" ? await readBetaSourceRoot() : nothing;
  await dispatch({
    type: "SupportDetected",
    support,
    manifestUrl: manifestUrlFor(channel),
    sourceRoot,
  });
  if (props.host === "window" && tauri) {
    unlistenCheck.current = just(
      await listenAboutCheckUpdates(() => {
        void dispatch({ type: "CheckRequested" });
      }),
    );
  }
});

onBeforeUnmount(() => {
  session.alive = false;
  void dispatch({ type: "InstallCancelled" });
  matchMaybe(unlistenCheck.current, {
    just: (unlisten) => {
      unlisten();
    },
    nothing: () => {},
  });
  unlistenCheck.current = nothing;
});
</script>

<template>
  <div class="about-panel" :class="`about-panel-${host}`">
    <div class="about-panel-identity">
      <img class="about-panel-logo" :src="logoUrl" alt="" />
      <div class="about-panel-text">
        <p class="about-panel-name">{{ APP_NAME }}</p>
        <p class="about-panel-version" data-testid="about-version">
          Version {{ version }}
        </p>
        <p class="about-panel-date" data-testid="about-release-date">
          {{ releaseLine }}
        </p>
      </div>
    </div>
    <div class="about-panel-actions">
      <ZwButton size="sm" data-testid="about-copy-version" @click="onCopy">
        {{ copyLabel }}
      </ZwButton>
      <ZwButton
        v-if="showLater"
        size="sm"
        data-testid="about-update-later"
        @click="onLater"
      >
        Later
      </ZwButton>
      <ZwButton
        v-if="showUpdates"
        class="about-panel-check"
        variant="primary"
        size="sm"
        data-testid="about-check-updates"
        :disabled="primary.enabled === false"
        :aria-busy="primaryBusy"
        @click="onPrimary"
      >
        <span
          v-if="primaryBusy"
          class="about-panel-spinner"
          aria-hidden="true"
        />
        {{ primary.label }}
      </ZwButton>
    </div>
    <p
      v-if="showUpdates"
      class="about-panel-status"
      data-testid="about-update-status"
      aria-live="polite"
    >
      {{ statusText }}
    </p>
    <details
      v-if="showErrorDetail"
      class="about-panel-error"
      data-testid="about-update-error"
    >
      <summary>Error details</summary>
      <p>{{ errorDetailText }}</p>
    </details>
  </div>
</template>

<style scoped>
.about-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  font-family: var(--font-ui);
  color: var(--text-strong);
}
.about-panel-identity {
  display: flex;
  align-items: center;
  gap: 0.85rem;
}
.about-panel-logo {
  width: 48px;
  height: 48px;
  flex: none;
}
.about-panel-text p {
  margin: 0;
}
.about-panel-name {
  font-size: 1.05rem;
  font-weight: 600;
}
.about-panel-version {
  font-size: 0.9rem;
  font-variant-numeric: tabular-nums;
}
.about-panel-date {
  font-size: 0.8rem;
  color: var(--text-muted);
}
.about-panel-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
}
.about-panel-check {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  width: 12.5rem;
  white-space: nowrap;
}
.about-panel-spinner {
  width: 0.75rem;
  height: 0.75rem;
  flex: none;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: about-panel-spin 0.7s linear infinite;
}
@keyframes about-panel-spin {
  to {
    transform: rotate(360deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .about-panel-spinner {
    animation: none;
    border-right-color: currentColor;
  }
}
.about-panel-status {
  margin: 0;
  min-height: 1.25em;
  font-size: 0.8rem;
  color: var(--text-muted);
}
.about-panel-error {
  font-size: 0.8rem;
  color: var(--text);
}
.about-panel-error summary {
  cursor: pointer;
  color: var(--text-muted);
}
.about-panel-error p {
  margin: 0.35rem 0 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
</style>
